<script setup lang="ts">
import FilePreviewDialog from '@/components/dialogs/FilePreviewDialog.vue';
import { FileType } from '@/enums/files';
import type { File } from '@/types/files';
import { ellipsifyString } from '@/utils/text';
import { ref } from 'vue';

const props = defineProps<{
    files: File[];
    actionPointId: string;
}>();

const selectedFile = ref<File>();
const isPreviewDialogVisible = ref(false);

function openPreview(fileName: string) {
    selectedFile.value = props.files.find((f) => f.name === fileName);
    isPreviewDialogVisible.value = true;
}
</script>

<template>
    <FilePreviewDialog :file="selectedFile" v-model="isPreviewDialogVisible" />

    <v-row>
        <v-col v-for="file in props.files" :key="file.name" cols="4" md="4" lg="3" class="pa-2">
            <transition name="fade" mode="out-in">
                <div v-if="file">
                    <v-img
                        v-if="file.type === FileType.Image"
                        :src="file.data"
                        width="100%"
                        height="auto"
                        class="cursor-pointer"
                        @click="openPreview(file.name)"
                    />
                    <video
                        v-else-if="file.type === FileType.Video"
                        :src="file.data"
                        class="border rounded-lg object-cover"
                        controls
                        width="100%"
                        height="150"
                    ></video>
                    <v-sheet
                        v-else-if="file.type === FileType.Pdf"
                        class="d-flex align-center justify-center border cursor-pointer pa-4"
                        height="125"
                        rounded="lg"
                        color="info"
                        @click="openPreview(file.name)"
                    >
                        <div class="text-center">
                            <v-icon icon="picture_as_pdf" size="50" class="mb-2" /><br />
                            <span class="file-title">
                                {{ ellipsifyString(file.name, 39) }}
                            </span>
                        </div>
                    </v-sheet>
                    <v-sheet
                        v-else
                        class="d-flex align-center justify-center border cursor-pointer pa-4"
                        height="150"
                        rounded="lg"
                        color="info"
                        @click="openPreview(file.name)"
                    >
                        <div class="text-center">
                            <v-icon icon="file_copy" size="50" class="mb-2" /><br />
                            <span class="file-title">
                                {{ ellipsifyString(file.name, 39) }}
                            </span>
                        </div>
                    </v-sheet>
                </div>

                <v-skeleton-loader
                    v-else
                    class="border-radius-fix"
                    :loading="true"
                    height="150"
                    width="100%"
                    rounded="lg"
                    type="image"
                    :aspect-ratio="1"
                />
            </transition>
        </v-col>
    </v-row>
</template>

<style scoped>
.file-title {
    font-size: 12px;
    white-space: wrap;
    overflow-wrap: anywhere;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.border-radius-fix {
    overflow: hidden;
    border-radius: 8px;
}
</style>
